import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useOutletContext } from 'react-router-dom';
import { Navbar, Container, NavDropdown } from 'react-bootstrap';
import Icon from '../common/Icons';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../hooks/useRole';
import { useToast } from '../../hooks/useToast';
import { getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import { ReceptionToastStack } from '../receptionist/ui';
import { ADMIN_COPY } from './adminCopy';
import { ADMIN_PATHS, getAdminNavSections } from './adminPaths';
import '../receptionist/ReceptionistDashboard.css';
import './AdminPortal.css';

const SIDEBAR_STORAGE_KEY = 'adm-sidebar-collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function AdminNavMenu({ sections, onNavigate, collapsed = false }) {
  return sections.map((section) => (
    <div key={section.id} className="adm-menu-section">
      <div className="adm-menu-section__label">{section.label}</div>
      <div className="adm-menu-section__items">
        {section.items.map(({ to, end, label, hint, icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) => `adm-menu-item${isActive ? ' is-active' : ''}`}
          >
            <span className="adm-menu-item__icon" aria-hidden>
              <Icon name={icon} size={18} />
            </span>
            <span className="adm-menu-item__text">
              <span className="adm-menu-item__row">
                <span className="adm-menu-item__label">{label}</span>
                {badge ? <span className="adm-menu-item__badge">{badge}</span> : null}
              </span>
              {hint ? <span className="adm-menu-item__hint">{hint}</span> : null}
            </span>
          </NavLink>
        ))}
      </div>
    </div>
  ));
}

/** Propage le toast admin vers les sous-routes (rapports, etc.). */
export function AdminContextOutlet() {
  const ctx = useOutletContext();
  return <Outlet context={ctx} />;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { canManageUsers, canManageCompany, canAccessReports, canAccessSettings } = useRole();
  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;
  const { toasts, showToast, removeToast, getDuration } = useToast();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const navSections = useMemo(
    () =>
      getAdminNavSections({
        canManageUsers: canManageUsers(),
        canManageCompany: canManageCompany(),
        canAccessReports: canAccessReports(),
        canAccessSettings: canAccessSettings()
      }),
    [canManageUsers, canManageCompany, canAccessReports, canAccessSettings]
  );

  const toast = useCallback(
    (variant, msg) => {
      showToast(msg, variant);
    },
    [showToast]
  );

  const outletValue = useMemo(() => ({ toast, showToast }), [toast, showToast]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileNav();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() || 'A';

  const userMenu = (
    <NavDropdown
      align="end"
      className="adm-user-menu"
      id="adm-user-menu"
      title={
        <span className="adm-user-menu__trigger" aria-label={ADMIN_COPY.ARIA_USER_MENU}>
          <span className="adm-user-menu__avatar" aria-hidden>
            {initials}
          </span>
          <span className="adm-user-menu__meta d-none d-md-flex">
            <span className="adm-user-menu__name">
              {user?.last_name}
              {user?.first_name ? ` ${user?.first_name}` : ''}
            </span>
            <span className="adm-user-menu__role">{getRoleLabel(user?.role)}</span>
          </span>
          <Icon name="chevronDown" size={14} className="adm-user-menu__chevron d-none d-md-block" aria-hidden />
        </span>
      }
    >
      <NavDropdown.Header className="adm-user-menu__header">
        <span className="adm-user-menu__header-avatar" aria-hidden>
          {initials}
        </span>
        <span className="adm-user-menu__header-text">
          <span className="adm-user-menu__header-name">
            {user?.last_name}
            {user?.first_name ? ` ${user?.first_name}` : ''}
          </span>
          {user?.email ? <span className="adm-user-menu__header-email">{user.email}</span> : null}
          <span className="adm-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
        </span>
      </NavDropdown.Header>
      <NavDropdown.Divider className="adm-user-menu__divider" />
      <NavDropdown.Item
        className="adm-user-menu__action adm-user-menu__action--danger"
        onClick={handleLogout}
      >
        <Icon name="signOut" size={16} aria-hidden />
        Déconnexion
      </NavDropdown.Item>
    </NavDropdown>
  );

  const layoutClassName = [
    'admin-portal',
    'admin-layout',
    'receptionist-dashboard',
    'receptionist-layout',
    'min-vh-100',
    'd-flex',
    'flex-column',
    sidebarCollapsed ? 'adm-sidebar-is-collapsed' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClassName}>
      <Navbar variant="dark" expand="lg" className="adm-topbar flex-shrink-0 recv-no-print shadow-sm">
        <Container fluid className="adm-topbar__container">
          <Navbar.Brand
            className="adm-topbar__brand-wrap mb-0"
            onClick={() => {
              closeMobileNav();
              navigate(ADMIN_PATHS.DASHBOARD);
            }}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
              alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
              className="adm-topbar__wordmark"
            />
            <span className="adm-topbar__brand">
              <span className="adm-topbar__brand-product">{ACCENT}</span>
              <span className="adm-topbar__brand-section">{ADMIN_COPY.LAYOUT.SIDE_MENU_SECTION}</span>
            </span>
          </Navbar.Brand>

          <div className="adm-topbar__toolbar ms-auto d-flex align-items-center gap-2">
            {userMenu}
            <button
              type="button"
              className={`adm-topbar__toggle d-lg-none navbar-toggler ${
                mobileNavOpen ? 'is-open' : ''
              }`}
              aria-controls="admin-mobile-drawer"
              aria-expanded={mobileNavOpen}
              aria-label={mobileNavOpen ? ADMIN_COPY.ARIA_CLOSE_MENU : ADMIN_COPY.ARIA_TOGGLE_MENU}
              onClick={() => (mobileNavOpen ? closeMobileNav() : setMobileNavOpen(true))}
            >
              {mobileNavOpen ? (
                <Icon name="times" size={20} className="adm-topbar__toggle-icon" aria-hidden />
              ) : (
                <span className="navbar-toggler-icon" />
              )}
            </button>
          </div>
        </Container>
      </Navbar>

      {mobileNavOpen ? (
        <>
          <button
            type="button"
            className="adm-mobile-drawer__backdrop recv-no-print"
            aria-label={ADMIN_COPY.ARIA_CLOSE_MENU}
            onClick={closeMobileNav}
          />
          <aside
            id="admin-mobile-drawer"
            className="adm-mobile-drawer recv-no-print"
            role="dialog"
            aria-modal="true"
            aria-label={ADMIN_COPY.MOBILE_NAV_TITLE}
          >
            <div className="adm-mobile-drawer__head">
              <span className="adm-mobile-drawer__title">{ADMIN_COPY.MOBILE_NAV_TITLE}</span>
              <button
                type="button"
                className="adm-mobile-drawer__close"
                aria-label={ADMIN_COPY.ARIA_CLOSE_MENU}
                onClick={closeMobileNav}
              >
                <Icon name="times" size={18} />
              </button>
            </div>
            <nav className="adm-sidebar__nav adm-mobile-drawer__nav" aria-label="Navigation principale">
              <AdminNavMenu sections={navSections} onNavigate={closeMobileNav} />
            </nav>
          </aside>
        </>
      ) : null}

      <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
        <aside
          className={`recv-sidebar adm-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
            sidebarCollapsed ? ' is-collapsed' : ''
          }`}
          aria-label={ADMIN_COPY.LAYOUT.SIDE_MENU_SECTION}
        >
          <nav className="adm-sidebar__nav" aria-label="Navigation principale">
            <AdminNavMenu sections={navSections} collapsed={sidebarCollapsed} />
          </nav>
          <div className="adm-sidebar__footer">
            <button
              type="button"
              className="adm-sidebar__toggle"
              onClick={toggleSidebar}
              aria-expanded={!sidebarCollapsed}
              aria-label={
                sidebarCollapsed ? ADMIN_COPY.ARIA_EXPAND_SIDEBAR : ADMIN_COPY.ARIA_TOGGLE_SIDEBAR
              }
            >
              <Icon
                name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'}
                size={18}
                aria-hidden
              />
              <span className="adm-sidebar__toggle-label">
                {sidebarCollapsed ? ADMIN_COPY.SIDEBAR_EXPAND : ADMIN_COPY.SIDEBAR_COLLAPSE}
              </span>
            </button>
          </div>
        </aside>

        <main className="recv-main flex-grow-1 overflow-auto">
          <Container fluid className="py-4 px-md-4 recv-dash-container adm-dash-container">
            <Outlet context={outletValue} />
          </Container>
        </main>
      </div>

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
